import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { of } from 'rxjs';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import { environment } from '../../environments/environment';

/**
 * Result of a sales-tax-rate computation.
 * Each value is the tax (in dollars) for the corresponding cost component.
 * Mirrors the server-side sales-tax rate response tuple
 * [manufacturingTax, packingTax, shippingTax, gatewayTax, softproofTax].
 *
 * The client is intentionally agnostic to whichever downstream sales-tax
 * service the server calls (and the server may choose different services
 * per jurisdiction). All vendor specifics live on the server.
 */
export class SalesTaxRateResult {
  manufacturingTax: number;
  packingTax: number;
  shippingTax: number;
  gatewayTax: number;
  softproofTax: number;
  /**
   * Opaque server-issued signature covering `(orgId, addressFingerprint, cost
   * inputs, tax outputs, exempt verdict)`. Echoed in the place-order envelope
   * under `signatures.salesTax` so the server can skip the duplicate TaxJar
   * call. Empty when the server hasn't started emitting it yet
   * (see `docs/contracts/outbox/2026-06-24-salestax-signature.md`).
   */
  signature: string;

  constructor(obj?: any) {
    this.manufacturingTax = 0;
    this.packingTax = 0;
    this.shippingTax = 0;
    this.gatewayTax = 0;
    this.softproofTax = 0;
    this.signature = '';
    if (obj) {
      if (Array.isArray(obj)) {
        // Tuple form: [manufacturingTax, packingTax, shippingTax, gatewayTax, softproofTax]
        this.manufacturingTax = Number(obj[0]) || 0;
        this.packingTax = Number(obj[1]) || 0;
        this.shippingTax = Number(obj[2]) || 0;
        this.gatewayTax = Number(obj[3]) || 0;
        this.softproofTax = Number(obj[4]) || 0;
      } else {
        for (const prop of Object.keys(obj)) {
          if (prop === 'signature') {
            this.signature = typeof obj[prop] === 'string' ? obj[prop] : '';
          } else {
            this[prop] = Number(obj[prop]) || 0;
          }
        }
      }
    }
  }

  get totalTax(): number {
    return this.manufacturingTax + this.packingTax + this.shippingTax + this.gatewayTax + this.softproofTax;
  }
}

export interface SalesTaxRateRequest {
  orgId: number;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  manufacturing: number;
  packing: number;
  shipping: number;
  gateway: number;
  softproof: number;
}

export interface SalesTaxOrganizationRequest {
  orgId: number;
  companyName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  /** Exempt-type code: '', 'wholesale', 'government', 'other', etc. */
  exemptType: string;
  /** Two-letter state codes where the organization is tax-exempt (comma-separated or array). */
  exemptStates: string | string[];
}

@Injectable()
export class SalesTaxService {
  public loadingState = 'INITIAL';

  constructor(private http: HttpClient, private messageService: MessageService, private startupService: StartupService) {
  }

  /**
   * Get the sales tax rate breakdown for a delivery address and a set of cost components.
   * Calls the server-side `salestax/rate` endpoint. The server is solely
   * responsible for selecting and calling the downstream sales-tax service.
   * @param orgId         The organization id placing the order
   * @param street        Street address (line 1)
   * @param city          City
   * @param state         State / region
   * @param zip           Postal code
   * @param country       Country (USA, etc.)
   * @param manufacturing Manufacturing cost in dollars
   * @param packing       Packing / packaging cost in dollars
   * @param shipping      Shipping cost in dollars
   * @param gateway       Payment-gateway cost in dollars
   * @param softproof     Softproof cost in dollars
   */
  getRate(orgId: number, street: string, city: string, state: string, zip: string, country: string,
          manufacturing: number, packing: number, shipping: number, gateway: number, softproof: number): Observable<SalesTaxRateResult> {
    const url = `${environment.apiBaseURL}salestax/rate`;
    const payload: SalesTaxRateRequest = {
      orgId: orgId,
      street: street,
      city: city,
      state: state,
      zip: zip,
      country: country,
      manufacturing: manufacturing,
      packing: packing,
      shipping: shipping,
      gateway: gateway,
      softproof: softproof
    };
    this.messageService.add(`SalesTaxService: getRate org=${orgId} zip=${zip}`);
    return this.http.post<any>(url, payload, this.startupService.getHttpOptions())
      .pipe(retry(2),
        map(response => new SalesTaxRateResult(response)),
        tap(_ => this.log(`got sales tax rate for zip=${zip}`)),
        catchError(this.handleError<SalesTaxRateResult>('getRate', new SalesTaxRateResult()))
      );
  }

  /**
   * Add or update the sales-tax-service registration for an organization
   * (nexus, exempt-state list). Mirrors the server endpoint `salestax/organization`.
   * The server picks and calls the downstream sales-tax service.
   * @param orgId        Organization id
   * @param companyName  Legal company name
   * @param street       Street address (line 1)
   * @param city         City
   * @param state        State / region
   * @param zip          Postal code
   * @param country      Country (USA, etc.)
   * @param exemptType   Exempt-type code ('', 'wholesale', 'government', 'other', ...)
   * @param exemptStates Two-letter state codes where the org is exempt (comma-separated string or array)
   */
  addOrganization(orgId: number, companyName: string, street: string, city: string, state: string,
                  zip: string, country: string, exemptType: string,
                  exemptStates: string | string[]): Observable<any> {
    const url = `${environment.apiBaseURL}salestax/organization`;
    const payload: SalesTaxOrganizationRequest = {
      orgId: orgId,
      companyName: companyName,
      street: street,
      city: city,
      state: state,
      zip: zip,
      country: country,
      exemptType: exemptType,
      exemptStates: exemptStates
    };
    this.messageService.add(`SalesTaxService: addOrganization org=${orgId}`);
    return this.http.post<any>(url, payload, this.startupService.getHttpOptions())
      .pipe(retry(2),
        tap(_ => this.log(`addOrganization org=${orgId} ok`)),
        catchError(this.handleError<any>('addOrganization'))
      );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';
      console.error(error, operation);
      this.log(`${operation} failed: ${error.message}`);
      return of(result as T);
    };
  }

  private log(message: string) {
    this.messageService.add('SalesTaxService: ' + message);
  }
}



